import { Form, Formik } from "formik";
import React, { useContext, useRef } from "react";
import * as yup from "yup";
import {
  Container,
  Paper,
  useTheme
} from "@mui/material";
import { useMediaQuery } from "@mui/material";
import Divider from "@mui/material/Divider";
import DataLayerService from "utility/dataLayerService";
import Validation from "utility/validation";
import styled from "@emotion/styled";
import TextInput from "components/atoms/TextInput";
import EmailInput from "components/atoms/EmailInput";
import SafetyLock from "components/atoms/SafetyLock";
import TermAndCondition from "components/atoms/TermAndCondition";
import TermOfServiceComponent from "components/atoms/TermOfService";
import { LeafButton } from "components/atoms/LeafButtons";
import LeafBox from "components/atoms/LeafBox";
import { useVersion, useCountry, useComponentName } from "hooks";
import ProgressBar from "components/atoms/ProgressBar";
import ProgressBarWithPercentage from 'components/atoms/ProgressBarWithPercentage';
import { scrollToTop, modalScrollToTop } from '../../../../utility/functions';
import { FormContext } from "App";
import PostService from "utility/postService";

// SVG icons.
import { ReactComponent as TrustPilotIcon } from "components/Forms/form-icons/trust-pilot.svg";
import { ReactComponent as BbbIcon } from "components/Forms/form-icons/BBB-a-plus-rating.svg";
import { ReactComponent as AngiIcon } from "components/Forms/form-icons/Angi.svg";
import { ReactComponent as TestimonialIcon } from "components/Forms/form-icons/testimonial-icon-lhws.svg";
import { ReactComponent as TrustedIcon } from "components/Forms/form-icons/trusted-icon-lhws.svg";

const CustomerDetailsStep = (props) => {
  const location = props.dataSet?.location ? props.dataSet.location : "multi-step-modal-form";
  const form = props.dataSet?.form ? props.dataSet.form : "modal";
  const discountText = props.dataSet?.discount ? props.dataSet.discount : "";
  const { setactiveLHWSStepIndex, formData, setFormData } = useContext(FormContext);
  const product = 'lhws';
  const country = useCountry();
  const userJourneyTracking = props.dataSet?.tracking || '';
  const componentName = props.component || 'ZipChecker';
  const formName = useComponentName(componentName);
  const subformName = useComponentName(props.modalComponent);
  const v2 = useVersion();
  const formRef = useRef(null);
  const customertestimonial = props.dataSet?.customertestimonial ?? '';
  const brandpartnerlogos = props.dataSet?.brandpartnerlogos ?? '';
  const trustpilotlogo = props.dataSet?.trustpilotlogo ?? '';
  const trustedcontent = props.dataSet?.trustedcontent ?? '';
  const secondEmailStep = props.dataSet?.secondemailstep ?? '';

  let firstName = '';
  let lastName = '';
  let emailAddress = '';

  const ValidationSchema = yup.object().shape({
    firstName: yup
      .string()
      .trim()
      .required('First Name is required.'),
    lastName: yup
      .string()
      .trim()
      .required('Last Name is required.'),
    emailAddress: yup
      .string()
      .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i, 'Email Address is not valid.')
      .required("Email Address is required."),
  });


  const buttonSubmit = (e) => {
    const firstNameValidation = Validation.FirstNameDataLayerValidation(firstName);
    const lastNameValidation = Validation.FirstNameDataLayerValidation(lastName);
    const EmailValidation = Validation.EmailDataLayerValidation(emailAddress);

    // Validation Error.
    const validationError = {
      ...firstNameValidation,
      ...lastNameValidation,
      ...EmailValidation
    };

    if (Object.keys(validationError).length !== 0) {
      DataLayerService.formFailEvent(form, location, validationError);

      modalScrollToTop(props.modalComponent);
    }

    // Scroll to the Top.
    scrollToTop(formRef);
  }

  const handleOnBlur = (event) => {
    switch (event.target.name) {
      case 'emailAddress':
        emailAddress = event.target.value;
      break;

      case 'firstName':
        firstName = event.target.value;
      break;

      case 'lastName':
        lastName = event.target.value;
      break;

      default:
      break;
    }

    // Passing lf.form.interaction event to DataLayer.
    DataLayerService.formInteractionEvent(form, location);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const StyledFormContainer = styled(Paper)(() => ({
    background: "#ffffff",
    borderRadius: 0,
    maxWidth: isMobile ? "100%" : 520,
    width: "100%",
    height: isMobile ? "auto" : 720,
  }));

  let email = '';

  return (
    <Formik
      initialValues={{
        firstName: firstName || '',
        lastName: lastName || '',
        emailAddress: email || '',
        termsOfService: false,
      }}
      validationSchema={ValidationSchema}
      onSubmit={(values) => {
        const { firstName, lastName, emailAddress } = values;
        const data = {
          ...formData,
          firstName: firstName,
          lastName: lastName,
          emailAddress: emailAddress,
        };

        const notes = `Type of Water:${formData.typeofwater}\n` +
          `Water Color:${(formData.waterColor === 1 ? 'Yes' : 'No')}\n` +
          `Financing: ${formData.financing}\n` +
          `Senior:${(formData.groupLevel?.includes("senior") ? "Yes" : "No")}\n` + // Use optional chaining
          `Veteran:${(formData.groupLevel?.includes("military") ? "Yes" : "No")}\n` + // Use optional chaining
          `House Type:${(formData.houseType !== undefined ? formData.houseType : "")}\n` +
          `Landing Page:${window.location.href}`;

        try {
          const sfmcData = {
            "firstName": firstName,
            "lastName": lastName,
            "email": emailAddress,
            "postalCode": formData.zipCode,
            "country": country,
            "businessUnit": product,
            "notes": notes,
            "created": new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
          };
          PostService.sendToSFMCAbandonment('sfmc-abandonment', sfmcData);
        } catch (error) {
          console.log(error);
          console.log("Error in sending data");
        }

        setFormData(data);
        if (secondEmailStep === 'enable') {
          setactiveLHWSStepIndex('group');
        }
        else {
          setactiveLHWSStepIndex('project');
        }

        // Scroll to the Top.
        scrollToTop(formRef);

        if (userJourneyTracking) {
          let stepNumber = 3;
          if (secondEmailStep === 'enable') {
            stepNumber = 2;
          }
          if ((componentName === 'ZipChecker') || (componentName === 'ModalForm')) {
            stepNumber = 4;
          }

          // Passing lf.form.status event to DataLayer.
          DataLayerService.formStatusEvent(formName, subformName, product, stepNumber, 4, 'notcompleted');
        }

        // Passing lf.form.start event to DataLayer.
        DataLayerService.formStartEvent(form, location, data);
      }}
    >
      {({ handleSubmit, touched, errors, isSubmitting }) => (
        <Container  maxWidth={isMobile ? "xs" : "sm"} disableGutters={true}>
          <StyledFormContainer>
              <Form
                className="flex flex-col justify-center items-start"
                onSubmit={handleSubmit}
                ref={formRef}
                onBlur={
                  (event) => {handleOnBlur(event)}
                }
              >
                {isSubmitting && Object.keys(errors).length > 0 && scrollToTop(formRef)}


                {trustpilotlogo === 'enable' && (
                  <div className="trust-pilot-logo">
                    <a
                      href="https://www.trustpilot.com/review/www.leafhomewatersolutions.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Read our reviews on Trustpilot"
                    >
                      <TrustPilotIcon
                        alt="Trustpilot logo"
                      />
                    </a>
                  </div>
                )}

                {trustedcontent === 'enable' && (
                  <div className="estimate-trust-section">
                    {/* Trusted Section */}
                    <div className="estimate-trust-section__badge-row">
                      <div className="estimate-trust-section__icon">
                        <TrustedIcon alt="Trusted Icon" />
                      </div>
                      <div>
                        <h2 className="estimate-trust-section__text">
                          Trusted by 1,000,000+ Homeowners
                        </h2>
                      </div>
                    </div>

                    {/* Personalize Section */}
                    <div>
                      <h2 className="estimate-trust-section__personalize-text">
                        Almost done! Let’s <span>personalize your water test.</span>
                      </h2>
                      <h3>
                        This helps us match you with your local home water experts.
                      </h3>
                    </div>
                  </div>
                )}

                {brandpartnerlogos === 'enable' && (
                  <div className="badge-container">
                    {/* Angi Badge */}
                    <div className="badge">
                      <AngiIcon
                        alt="Angi"
                      />
                    </div>

                    {/* Trustpilot Badge */}
                    <div className="badge">
                      <a
                        href="https://www.trustpilot.com/review/www.leafhomewatersolutions.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Read 75,000+ reviews on Trustpilot"
                      >
                        <TrustPilotIcon
                          alt="Trustpilot rating 4.5 stars"
                        />
                      </a>
                    </div>

                    {/* HomeAdvisor Badge */}
                    <div className="badge">
                      <BbbIcon
                        alt="BBB A+ Rating"
                      />
                    </div>
                  </div>
                )}

                {customertestimonial === 'top' && (
                  <div className="testimonial">
                    <div className="testimonial-content">
                      <p className="quote">
                        “We now have soft, tasteless, odorless, clear water. Best decision ever.”
                      </p>
                      <div className="rating-location">
                        <span className="stars" aria-label="5 out of 5 stars" role="img">★★★★★</span>
                      </div>
                    </div>
                    <div className="testimonial-photo">
                      <TestimonialIcon alt="Photo of happy LeafHome Water Solution customer" />
                    </div>
                  </div>
                )}

                {trustedcontent !== 'enable' && (
                  <h3 className="form-step-title text-2xl fw-700">
                    Who are we personalizing this <br/>water test for?
                  </h3>
                )}

                <div className="flex flex-col items-start mb-2 label-bold">
                   <TextInput touched={touched} errors={errors} fieldName={'firstName'} label={'First Name'} placeholder={v2 ? "First Name" : "E.g., First Name"} />
                </div>
                <Divider className="divider" />
                <div className="flex flex-col items-start mb-2 label-bold">
                   <TextInput touched={touched} errors={errors} fieldName={'lastName'} label={'Last Name'} placeholder={v2 ? "Last Name" : "E.g., Last Name"} />
                </div>
                <Divider className="divider" />

                <div className="flex flex-col items-start mb-2 label-bold">
                  <EmailInput touched={touched} errors={errors} fieldName={'emailAddress'} />
                </div>

                {country === 'ca' ? (
                  <TermOfServiceComponent />
                ) : ''}

                <Divider className="divider" />

                {secondEmailStep === '' && (
                  (formData.groupLevel.length > 0 &&
                    (formData.groupLevel.includes("senior") ||
                     formData.groupLevel.includes("military"))) ||
                  discountText ? (
                    <>
                      <h3 className="discount2-title">
                        Applied discounts for your estimate:
                      </h3>
                      <div className="discount-text">
                        <ul>
                          {discountText && (
                            <li>
                              <h4 className="fw-normal">{discountText}</h4>
                            </li>
                          )}
                          {(formData.groupLevel.length > 0 &&
                            (formData.groupLevel.includes("senior") ||
                             formData.groupLevel.includes("military"))) && (
                            <li>
                              <h4 className="fw-normal">10% Senior/Military Discount</h4>
                            </li>
                          )}
                        </ul>
                      </div>
                    </>
                  ) : null
                )}

                <LeafBox style={{
                  marginTop: 2,
                  marginBottom: 3,
                }}>
                  <LeafButton
                    buttonType={"primary"}
                    type="submit"
                    handleClick={buttonSubmit}
                    isSubmitting={isSubmitting}
                  >
                    Next
                  </LeafButton>
                  <SafetyLock {...props} />

                  { v2 ?
                    <ProgressBarWithPercentage {...props} />
                    :
                    <ProgressBar customClass="" {...props} />
                  }

                  {customertestimonial === 'bottom' && (
                    <div className="testimonial">
                      <div className="testimonial-content">
                        <p className="quote">
                          “We now have soft, tasteless, odorless, clear water. Best decision ever.”
                        </p>
                        <div className="rating-location">
                          <span className="stars" aria-label="5 out of 5 stars" role="img">★★★★★</span>
                        </div>
                      </div>
                      <div className="testimonial-photo">
                        <TestimonialIcon alt="Photo of happy LeafHome Water Solution customer from FL" />
                      </div>
                    </div>
                  )}

                  <TermAndCondition />
                </LeafBox>
              </Form>
            </StyledFormContainer>
        </Container>
      )}
    </Formik>
  );
}

export default CustomerDetailsStep;
